import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Check, X, ChevronDown, ShieldCheck } from 'lucide-react';
import { usePricing } from '../contexts/PricingContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { BuyButton } from '../components/shared/BuyButton';
import { CouponInput } from '../components/shared/CouponInput';
import { Link } from 'react-router-dom';

interface CouponResult {
  valid: boolean;
  discountPercent: number;
  newPrice: number;
  code: string;
}

const COMPARE_ROWS = [
  { feature: 'Mapa Natal completo',      free: false,    monthly: true,        annual: true },
  { feature: 'Previsão diária',          free: true,     monthly: true,        annual: true },
  { feature: 'Previsão semanal/mensal',  free: false,    monthly: true,        annual: true },
  { feature: 'Previsão anual',           free: false,    monthly: false,       annual: true },
  { feature: 'Tarot (tiragens)',         free: '1/mês',  monthly: 'Ilimitado', annual: 'Ilimitado' },
  { feature: 'Sinastria',                free: false,    monthly: '3/mês',     annual: 'Ilimitada' },
  { feature: 'AstroMatch',              free: false,    monthly: true,        annual: true },
  { feature: 'Numerologia',              free: false,    monthly: true,        annual: true },
  { feature: 'I Ching',                  free: true,     monthly: true,        annual: true },
  { feature: 'Astrologia Védica',        free: false,    monthly: true,        annual: true },
  { feature: 'Human Design',             free: false,    monthly: true,        annual: true },
  { feature: 'Astrocartografia',         free: false,    monthly: '1/mês',     annual: '3/mês' },
  { feature: 'Astro Coach',              free: false,    monthly: false,       annual: true },
  { feature: 'Histórico ilimitado',      free: false,    monthly: true,        annual: true },
  { feature: 'Exportação PDF',           free: false,    monthly: true,        annual: true },
];

function CellValue({ v }: { v: boolean | string }) {
  if (v === true)  return <Check size={16} style={{ color: '#4ade80' }} aria-label="Sim" />;
  if (v === false) return <X    size={14} style={{ color: 'var(--text-muted)' }} aria-label="Não" />;
  return <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{v}</span>;
}

const SEALS = [
  { icon: '🔒', label: 'Garantia 7 dias' },
  { icon: '💳', label: 'Stripe + Pix' },
  { icon: '🛡️', label: 'LGPD' },
  { icon: '⚡', label: 'Cancele 1 clique' },
];

/* TODO_REAL_TESTIMONIAL */
const TESTIMONIALS = [
  { text: 'Valia cada centavo. Em um mês de Premium entendi mais sobre mim mesma do que em anos de terapia.', name: 'Ana B.', detail: '31 anos · Curitiba · ☉ Touro' },
  { text: 'A previsão semanal personalizada mudou como eu planejo minha semana. Sabe quando o céu "conspira" e você vê no app?', name: 'Carlos M.', detail: '27 anos · São Paulo · ☉ Sagitário' },
  { text: 'Cancelei Netflix e assine a Professya. Melhor decisão do ano. Minha Lua em Câncer agradece.', name: 'Fernanda R.', detail: '35 anos · Rio de Janeiro · ☉ Capricórnio' },
];

const PRICING_FAQ = [
  { q: 'Posso cancelar quando quiser?', a: 'Sim. Você pode cancelar pelo app ou site em menos de 1 clique. Sem burocracia, sem formulários.' },
  { q: 'Como funciona o reembolso de 7 dias?', a: 'Se em 7 dias você não estiver satisfeito, devolvemos 100% do valor pago. Basta entrar em contato por e-mail ou chat.' },
  { q: 'O que muda no plano anual vs mensal?', a: 'O plano anual inclui tudo do mensal mais: Previsão Anual, Sinastria ilimitada, Astrocartografia 3x/mês, e o programa completo de Astro Coach (12 semanas).' },
  { q: 'Já pago mensal. Posso migrar pro anual?', a: 'Sim. Ao assinar o anual, seu plano mensal é cancelado automaticamente e o valor é calculado pro-rata. Você não paga duas vezes.' },
  { q: 'Tem desconto para estudantes?', a: 'Não temos desconto estudantil automatizado, mas temos programas de indicação (ganhe 1 mês grátis para cada amigo que assinar) e cupons sazonais. Siga no Instagram @professya para não perder.' },
];

export function PricingPage() {
  const { getProduct, loading } = usePricing();
  const { format }              = useCurrency();
  const [coupon,     setCoupon]     = useState<CouponResult | null>(null);
  const [couponCode, setCouponCode] = useState<string | undefined>();
  const [tableOpen,  setTableOpen]  = useState(false);
  const [faqOpen,    setFaqOpen]    = useState<number | null>(null);

  const annual  = getProduct('annual_sub');
  const monthly = getProduct('monthly_sub');

  const annualPrice  = annual  ? (coupon ? coupon.newPrice : annual.price_brl)  : 0;
  const monthlyPrice = monthly ? monthly.price_brl : 0;
  const savePct      = annual && monthlyPrice > 0 ? Math.round((1 - annualPrice / (monthlyPrice * 12)) * 100) : 0;

  if (loading) {
    return (
      <div className="cosmic-page" style={{ minHeight: '100vh' }}>
        <div className="page-container">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {[1, 2, 3].map(i => <div key={i} className="card shimmer" style={{ height: '220px' }} />)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Planos e Preços, Professya</title>
        <meta name="description" content="Assine a Professya e acesse mapa natal completo, previsões personalizadas, sinastria, tarot e muito mais. Garantia de 7 dias." />
        <link rel="canonical" href="https://professya.com/pricing" />
      </Helmet>

      <div className="cosmic-page" style={{ minHeight: '100vh', paddingBottom: '4rem' }}>
      <div className="page-container" style={{ paddingTop: '3rem' }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
          <h1 className="font-display" style={{ fontSize: 'var(--text-display-lg)', color: 'var(--text-primary)', margin: '0 0 0.75rem' }}>
            Escolha como você quer voar
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-body-lg)', margin: 0 }}>
            3.000 anos de sabedoria astrológica. Começe grátis hoje.
          </p>
        </div>

        {/* 3 pricing cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1.25rem', marginBottom: '2rem', alignItems: 'start' }}>

          {/* Free */}
          <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            <div>
              <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', margin: '0 0 0.25rem' }}>Grátis</p>
              <p style={{ fontFamily: 'var(--font-display)', fontSize: '2rem', color: 'var(--text-primary)', margin: 0, fontWeight: 700 }}>
                R$ 0 <span style={{ fontSize: 'var(--text-caption)', fontFamily: 'var(--font-ui)', color: 'var(--text-muted)', fontWeight: 400 }}>/ sempre</span>
              </p>
            </div>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {['1 mapa natal', 'Previsão diária', '3 perguntas/mês', 'Tarot básico'].map(f => (
                <li key={f} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: 'var(--text-body)', color: 'var(--text-secondary)' }}>
                  <Check size={15} style={{ color: '#4ade80', flexShrink: 0 }} aria-hidden /> {f}
                </li>
              ))}
            </ul>
            <Link to="/register" className="btn btn-secondary" style={{ textAlign: 'center', marginTop: 'auto' }}>
              Continuar grátis
            </Link>
          </div>

          {/* Premium Monthly, popular */}
          <div className="card-gold" style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: '1rem', right: '1rem' }}>
              <span className="badge badge-premium">MAIS ESCOLHIDO</span>
            </div>
            <div>
              <p style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', margin: '0 0 0.25rem' }}>Premium</p>
              <p style={{ fontFamily: 'var(--font-display)', fontSize: '2rem', color: 'var(--petrol)', margin: 0, fontWeight: 700 }}>
                {monthly ? format(monthlyPrice) : '...'} <span style={{ fontSize: 'var(--text-caption)', fontFamily: 'var(--font-ui)', color: 'var(--petrol)', fontWeight: 400 }}>/mês</span>
              </p>
            </div>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {['Tudo ilimitado', 'Mapa natal completo', 'Sinastria 3/mês', 'Histórico + PDF'].map(f => (
                <li key={f} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: 'var(--text-body)', color: 'var(--text-secondary)' }}>
                  <Check size={15} style={{ color: 'var(--petrol)', flexShrink: 0 }} aria-hidden /> {f}
                </li>
              ))}
            </ul>
            {monthly && <BuyButton productId="monthly_sub" label={`Começar Premium`} />}
          </div>

          {/* Annual, best value */}
          <div className="card-gold" style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', position: 'relative', overflow: 'hidden', boxShadow: 'var(--shadow-card-hover)' }}>
            <div style={{ position: 'absolute', top: '1rem', right: '1rem' }}>
              <span className="badge badge-gold">MELHOR VALOR</span>
            </div>
            <div>
              <p style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', margin: '0 0 0.25rem' }}>Premium Anual</p>
              <p style={{ fontFamily: 'var(--font-display)', fontSize: '2rem', color: 'var(--petrol)', margin: 0, fontWeight: 700 }}>
                {annual ? format(annualPrice) : '...'} <span style={{ fontSize: 'var(--text-caption)', fontFamily: 'var(--font-ui)', color: 'var(--petrol)', fontWeight: 400 }}>/ano</span>
              </p>
              {annual && monthlyPrice > 0 && (
                <p style={{ margin: '0.25rem 0 0', fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>
                  ≈ {format(annualPrice / 12)}/mês · economize {savePct}%
                  {coupon && <span style={{ color: '#4ade80', marginLeft: '0.4rem' }}>({coupon.discountPercent}% cupom)</span>}
                </p>
              )}
            </div>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {['Tudo do Premium', 'Previsão anual', 'Sinastria ilimitada', 'Astro Coach completo'].map(f => (
                <li key={f} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: 'var(--text-body)', color: 'var(--text-secondary)' }}>
                  <Check size={15} style={{ color: 'var(--petrol)', flexShrink: 0 }} aria-hidden /> {f}
                </li>
              ))}
            </ul>
            {annual && (
              <>
                <BuyButton productId="annual_sub" couponCode={couponCode} label={`✦ Assinar por ${format(annualPrice)}/ano`} />
                {/* Coupon input inline */}
                <div style={{ marginTop: '-0.5rem' }}>
                  <CouponInput productPrice={annual.price_brl} onApply={r => { setCoupon(r); setCouponCode(r?.code); }} />
                </div>
              </>
            )}
          </div>
        </div>

        {/* Guarantee seals */}
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '1.5rem', marginBottom: '2.5rem', padding: '1.25rem', background: 'var(--bg-surface)', borderRadius: 'var(--radius-card)', border: '1px solid var(--border-subtle)' }}>
          {SEALS.map(s => (
            <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: 'var(--text-body-sm)', color: 'var(--text-secondary)' }}>
              <span aria-hidden>{s.icon}</span>
              {s.label}
            </div>
          ))}
        </div>

        {/* Comparison table, collapsible */}
        <div style={{ marginBottom: '2.5rem' }}>
          <button
            onClick={() => setTableOpen(o => !o)}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--petrol)', fontSize: 'var(--text-body)', fontWeight: 600, padding: '0.5rem 0', width: '100%', justifyContent: 'center' }}
            aria-expanded={tableOpen}
          >
            {tableOpen ? 'Ocultar comparação' : 'Ver comparação detalhada'}
            <ChevronDown size={16} aria-hidden style={{ transform: tableOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform var(--transition-fast)' }} />
          </button>

          {tableOpen && (
            <div className="animate-fade-in" style={{ marginTop: '1rem', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-card)', overflow: 'hidden' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr repeat(3, 80px)', background: 'var(--bg-elevated)', padding: '0.625rem 1rem', borderBottom: '1px solid var(--border-subtle)', gap: '0.5rem' }}>
                <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>Funcionalidade</span>
                {['Grátis', 'Premium', 'Anual'].map(h => (
                  <span key={h} style={{ fontSize: 'var(--text-caption)', color: h === 'Grátis' ? 'var(--text-muted)' : 'var(--petrol)', fontWeight: 600, textAlign: 'center' }}>{h}</span>
                ))}
              </div>
              {COMPARE_ROWS.map(row => (
                <div key={row.feature} style={{ display: 'grid', gridTemplateColumns: '1fr repeat(3, 80px)', padding: '0.5rem 1rem', borderBottom: '1px solid var(--border-subtle)', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontSize: 'var(--text-body-sm)', color: 'var(--text-secondary)' }}>{row.feature}</span>
                  {([row.free, row.monthly, row.annual] as (boolean | string)[]).map((v, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'center' }}>
                      <CellValue v={v} />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Credit packs */}
        <div style={{ marginBottom: '2.5rem' }}>
          <h2 className="font-display" style={{ fontSize: 'var(--text-display-sm)', color: 'var(--text-primary)', margin: '0 0 0.5rem', textAlign: 'center' }}>
            Prefere pagar conforme usar?
          </h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-body-sm)', marginBottom: '1.5rem', textAlign: 'center' }}>
            Créditos avulsos, sem assinatura
          </p>

          {monthly && (
            <div style={{ marginBottom: '1.25rem', padding: '0.875rem 1rem', borderRadius: 'var(--radius-card)', border: '1px solid rgba(31,78,95,0.3)', background: 'rgba(31,78,95,0.05)', fontSize: 'var(--text-caption)', color: 'var(--text-secondary)', lineHeight: 1.55 }}>
              💡 <strong style={{ color: 'var(--text-primary)' }}>Dica:</strong> O plano Premium por <strong style={{ color: 'var(--petrol)' }}>{format(monthlyPrice)}/mês</strong> inclui todos esses serviços sem precisar comprar créditos.
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {['questions_q3', 'astromatch_a3', 'synastry_s1', 'questions_q5', 'astromatch_a10', 'synastry_s3', 'questions_q10'].map(id => {
              const p = getProduct(id);
              if (!p) return null;
              const perUnit = p.price_brl / (p.credits ?? 1);
              return (
                <div key={id} className="card" style={{ display: 'flex', alignItems: 'center', gap: '1rem', justifyContent: 'space-between' }}>
                  <div style={{ minWidth: 0 }}>
                    <p style={{ fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 0.15rem', fontSize: 'var(--text-label)' }}>{p.name}</p>
                    <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', margin: 0 }}>{format(perUnit)}/unidade</p>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexShrink: 0 }}>
                    <span style={{ fontWeight: 700, color: 'var(--petrol)', fontFamily: 'var(--font-display)' }}>{format(p.price_brl)}</span>
                    <BuyButton productId={id} className="btn btn-primary btn-sm" label="Comprar" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Testimonials */}
        <div style={{ marginBottom: '2.5rem' }}>
          <p style={{ textAlign: 'center', fontSize: 'var(--text-caption)', color: 'var(--petrol)', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '1.5rem' }}>
            O que dizem os assinantes
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1rem' }}>
            {TESTIMONIALS.map(t => (
              <div key={t.name} className="card" style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
                <div style={{ color: 'var(--petrol)', fontSize: '13px', letterSpacing: '2px' }}>★★★★★</div>
                <p style={{ fontFamily: 'var(--font-reading)', fontSize: 'var(--text-body)', color: 'var(--text-primary)', lineHeight: 1.7, margin: 0, flex: 1 }}>"{t.text}"</p>
                <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: '0.75rem' }}>
                  <p style={{ margin: 0, fontSize: 'var(--text-label)', color: 'var(--text-primary)', fontWeight: 600 }}>{t.name}</p>
                  <p style={{ margin: 0, fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>{t.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Pricing FAQ */}
        <div>
          <h2 className="font-display" style={{ fontSize: 'var(--text-display-sm)', color: 'var(--text-primary)', margin: '0 0 1.5rem', textAlign: 'center' }}>
            Perguntas sobre planos
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxWidth: '640px', margin: '0 auto' }}>
            {PRICING_FAQ.map((item, i) => (
              <div key={i} style={{ border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
                <button
                  onClick={() => setFaqOpen(faqOpen === i ? null : i)}
                  aria-expanded={faqOpen === i}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', padding: '1rem 1.25rem', background: faqOpen === i ? 'rgba(31,78,95,0.06)' : 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', transition: 'background var(--transition-fast)' }}
                >
                  <span style={{ fontSize: 'var(--text-body)', color: 'var(--text-primary)', fontWeight: 500 }}>{item.q}</span>
                  <ChevronDown size={16} aria-hidden style={{ color: 'var(--petrol)', flexShrink: 0, transform: faqOpen === i ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform var(--transition-fast)' }} />
                </button>
                {faqOpen === i && (
                  <div style={{ padding: '0 1.25rem 1rem' }}>
                    <p style={{ margin: 0, fontSize: 'var(--text-body)', color: 'var(--text-secondary)', lineHeight: 1.7 }}>{item.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Final guarantee */}
          <div style={{ textAlign: 'center', marginTop: '2.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', color: 'var(--text-muted)', fontSize: 'var(--text-body-sm)' }}>
            <ShieldCheck size={16} style={{ color: 'var(--petrol)' }} aria-hidden />
            Garantia de 7 dias · Se não amar, devolvemos. Sem perguntas.
          </div>
        </div>

      </div>
      </div>
    </>
  );
}
